import {
  ApplicationRef,
  createComponent,
  EmbeddedViewRef,
  EnvironmentInjector,
  inject,
  inputBinding,
  Service,
} from '@angular/core';
import Shepherd, {
  type StepOptions,
  type Tour,
  type TourOptions,
} from 'shepherd.js';
import { toSignal } from '@angular/core/rxjs-interop';
import { translate } from '@thiememeulenhoff/translations';
import { GuidedTourProgressBarComponent } from './guided-tour-progress-bar/guided-tour-progress-bar.component';
import { LayoutService } from '../layout';
import { Event, EventBus } from '../rxjs/event-bus';
import { makeButton } from './guided-tour-buttons/guided-tour-buttons';
import { GuidedTourStep } from './guided-tour.enum';

export const STEPS_BUTTONS = {
  start: {
    text: translate('tour.start'),
    type: GuidedTourStep.Next,
  },
  back: {
    secondary: true,
    text: translate('tour.back'),
    type: GuidedTourStep.Back,
  },
  cancel: {
    secondary: true,
    text: translate('tour.skip'),
    type: GuidedTourStep.Cancel,
  },
  cancelStep: {
    secondary: true,
    text: translate('tour.skip-step'),
    type: GuidedTourStep.Cancel,
  },
  finish: {
    text: translate('tour.complete'),
    type: GuidedTourStep.Complete,
  },
  next: {
    text: translate('tour.next'),
    type: GuidedTourStep.Next,
  },
};

export interface GuidedTourEvent {
  tourName: string;
  stepId?: string;
  action: 'start' | 'cancel' | 'show' | 'complete' | 'next' | 'back';
}

export const defaultStepOptions = {
  scrollTo: true,
  scrollToHandler: (element: HTMLElement) => {
    if (!element) return;

    const yOffset = -250; // scroll 250px above the element, extra height for header
    const y =
      element.getBoundingClientRect().top + window.pageYOffset + yOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
  },
  cancelIcon: {
    enabled: true,
  },
};

@Service()
export class GuidedTourService {
  #appRef = inject(ApplicationRef);

  readonly #layoutService = inject(LayoutService);

  #eventBus = new EventBus<GuidedTourEvent | undefined>();

  #confirmCancel: TourOptions['confirmCancel'] = false;

  #confirmCancelMessage?: TourOptions['confirmCancelMessage'];

  #environmentInjector = inject(EnvironmentInjector);

  #defaultStepOptions = defaultStepOptions;

  #exitOnEsc: TourOptions['exitOnEsc'] = true;

  #keyboardNavigation: TourOptions['keyboardNavigation'] = true;

  #tourName: TourOptions['tourName'] = undefined;

  tourObject!: Tour;

  readonly isMobile = toSignal(this.#layoutService.isMobile$);

  setEventBus(eventBus: EventBus<GuidedTourEvent | undefined>) {
    this.#eventBus = eventBus;
  }

  #initialize() {
    if (this.isMobile()) {
      console.warn('Tour is not available on mobile devices');
    }

    const tourObject = new Shepherd.Tour({
      confirmCancel: this.#confirmCancel,
      confirmCancelMessage: this.#confirmCancelMessage,
      classPrefix: 'tm-tour-',
      defaultStepOptions: this.#defaultStepOptions,
      keyboardNavigation: this.#keyboardNavigation,
      tourName: this.#tourName,
      useModalOverlay: true,
      exitOnEsc: this.#exitOnEsc,
    });

    this.tourObject = tourObject as Tour;

    tourObject.on('cancel', this.#onCancel.bind(this));
    tourObject.on('complete', this.#onComplete.bind(this));
  }

  constructor() {
    this.#initialize();
  }

  back() {
    if (!this.tourObject || this.#tourName === undefined) {
      throw new Error('No started tour found to perform back action');
    }

    this.tourObject.back();

    this.#eventBus.emit(`tour`, {
      tourName: this.#tourName,
      stepId: this.tourObject.getCurrentStep()?.id,
      action: 'back',
    });
  }

  cancel() {
    this.tourObject.cancel();
  }

  #onCancel() {
    if (!this.tourObject || this.#tourName === undefined) {
      throw new Error('No started tour found to perform cancel action');
    }

    this.#eventBus.emit(`tour`, {
      tourName: this.#tourName,
      stepId: this.tourObject.getCurrentStep()?.id,
      action: 'cancel',
    });

    // Reset totalSteps after tour cancellation
    this.tourObject.steps = [];
  }

  #onComplete() {
    if (!this.tourObject || this.#tourName === undefined) {
      throw new Error('No started tour found to perform complete action');
    }

    this.#eventBus.emit(`tour`, {
      tourName: this.#tourName,
      stepId: this.tourObject.getCurrentStep()?.id,
      action: 'complete',
    });

    // Reset totalSteps after tour completion
    this.tourObject.steps = [];
  }

  complete() {
    this.tourObject.complete();
  }

  next() {
    if (!this.tourObject || this.#tourName === undefined) {
      throw new Error('No started tour found to perform next action');
    }
    this.tourObject.next();

    this.#eventBus.emit(`tour`, {
      tourName: this.#tourName,
      stepId: this.tourObject.getCurrentStep()?.id,
      action: 'next',
    });
  }

  start(tourName: string, steps: Array<StepOptions>, startPosition?: number) {
    if (!this.isMobile()) {
      this.#tourName = tourName;

      if (steps.length === 0) {
        throw new Error('No steps provided to start the tour');
      }

      // Remove steps of a previously started tour, otherwise they are shown first
      this.tourObject.steps = [];

      this.#addSteps(steps);

      // start on a specific position / id
      if (startPosition === undefined) {
        this.tourObject.show(startPosition);

        this.#eventBus.emit(`tour`, {
          tourName,
          stepId: this.tourObject.getCurrentStep()?.id,
          action: 'next',
        });
      } else {
        this.tourObject.start();

        this.#eventBus.emit(`tour`, {
          tourName,
          stepId: this.tourObject.getCurrentStep()?.id,
          action: 'start',
        });
      }
    }
  }

  #addSteps(steps: Array<StepOptions>) {
    const tour = this.tourObject;

    steps.forEach((step) => {
      const newStep = {
        ...step,
        buttons: step.buttons
          ? step.buttons.map(makeButton.bind(this), this)
          : [],
        modalOverlayOpeningRadius: 10,
        modalOverlayOpeningPadding: 10,
        when: {
          show: () => {
            const currentStep = tour.getCurrentStep();

            if (currentStep) {
              const progressBarComponentRef = createComponent(
                GuidedTourProgressBarComponent,
                {
                  environmentInjector: this.#environmentInjector,
                  bindings: [
                    inputBinding('currentStep', () => currentStep.id),
                    inputBinding('totalSteps', () => tour.steps.length),
                  ],
                },
              );

              const viewRef =
                progressBarComponentRef.hostView as EmbeddedViewRef<unknown>;

              this.#appRef.attachView(viewRef);

              const element = viewRef.rootNodes[0] as HTMLElement;

              const currentStepElement = currentStep.el as HTMLElement | null;
              if (!currentStepElement) return;

              const shepherdText =
                currentStepElement.querySelector('.shepherd-text');
              if (!shepherdText) return;

              shepherdText.appendChild(element);
            }
          },
        },
      };
      tour.addStep(newStep);
    });
  }

  on(
    key: string,
    callback: (data: Event<GuidedTourEvent | undefined>) => void,
  ) {
    return this.#eventBus.on(key, callback);
  }
}
